import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import { checkData, deleteItem, updateData } from "../../actions/actionItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues } from "../../../constants/Types";
import { GolfPropertyEnum } from "../../../constants/NavbarList";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import AlertComponent from "../../subcomponents/AlertComponent";
import {
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import {
  RETAIL_SUB_CATEGORY_EMPTY_LIST_MESSAGE,
  RETAIL_SUB_CATEGORY_FIELD_NAME,
  RETAIL_SUB_CATEGORY_LABEL_ACTIVE,
  RETAIL_SUB_CATEGORY_LABEL_NAME,
  RETAIL_SUB_CATEGORY_LABEL_NUMBER,
  RETAIL_SUB_CATEGORY_LEVELS,
  RETAIL_SUB_CATEGORY_LIST_TITLE,
  RETAIL_SUB_CATEGORY_BUTTON_SUFFIX,
  RETAIL_SUB_CATEGORY_PAGE_TITLE,
  RETAIL_SUB_CATEGORY_SAMPLE_WARNING,
  RETAIL_SUB_CATEGORY_SELECT_LEVEL_PLACEHOLDER,
  RETAIL_SUB_CATEGORY_TOOLTIPS,
  RETAIL_SUB_CATEGORY_VALIDATION_MESSAGES,
  RetailSubCategoryLevel,
  buildRetailSubCategoryPayload,
  formatRetailSubCategoriesForDnd,
  getRetailSubCategoryLevelLabel,
  getRetailSubCategoryNameFromRow,
  isRetailSubCategoryDuplicateEntry,
  isRetailSubCategoryRowActive,
  parseRetailSubCategoryLevelFromRow,
  RETAIL_SUB_CATEGORY_DND_LEVEL_LABEL,
  stripRetailSubCategoryDndDisplayFields,
} from "../../../constants/RetailSubCategoriesFields";

const RetailSubCategories = ({
  data,
  fieldValues,
}: {
  data: (value: any) => void;
  fieldValues: any;
}) => {
  const [subCategoryNumber, setSubCategoryNumber] = useState<
    RetailSubCategoryLevel | ""
  >("");
  const [subCategoryName, setSubCategoryName] = useState<string | null>(null);
  const [active, setActive] = useState<boolean>(true);
  const [nameError, setNameError] = useState<boolean>(false);
  const [nameErrorMessage, setNameErrorMessage] = useState<string>("");
  const [numberError, setNumberError] = useState<boolean>(false);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [items, setItems] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);

  useEffect(() => {
    fieldValues && setItems(Array.isArray(fieldValues) ? fieldValues : []);
  }, []);

  useEffect(() => {
    data(items);
    setSelectedId(checkData(items, selectedId) ? selectedId : null);
  }, [items]);

  const validateFields = (): boolean => {
    if (subCategoryNumber === "") {
      setAlertMessage(RETAIL_SUB_CATEGORY_VALIDATION_MESSAGES.numberMandatory);
      setAlertType("error");
      setOpenAlert(true);
      setNumberError(true);
      return false;
    }
    setNumberError(false);

    if (!subCategoryName || subCategoryName.trim().length === 0) {
      setAlertMessage(RETAIL_SUB_CATEGORY_VALIDATION_MESSAGES.nameMandatory);
      setAlertType("error");
      setOpenAlert(true);
      setNameError(true);
      return false;
    }

    setNameError(false);
    setNameErrorMessage("");
    if (
      !validateString(
        subCategoryName,
        validationLength.subCategoryName,
        validationType.SPACE,
      )
    ) {
      setNameError(true);
      setNameErrorMessage(validationMessages.subCategoryName);
      return false;
    }

    if (
      isRetailSubCategoryDuplicateEntry(
        items,
        subCategoryNumber,
        subCategoryName,
        selectedId,
      )
    ) {
      setAlertMessage(RETAIL_SUB_CATEGORY_VALIDATION_MESSAGES.duplicateEntry);
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }

    return true;
  };

  const updateItem = () => {
    if (!validateFields()) {
      return false;
    }
    setItems((prev) =>
      prev.map((item: any) =>
        String(item.id) === String(selectedId) ? { ...item, ...payload } : item,
      ),
    );
    editItem(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(
      `${GolfPropertyEnum.retailSubCategories} updated successfully`,
    );
    setAlertType("success");
    setOpenAlert(true);
  };

  const addItem = () => {
    if (!validateFields()) {
      return false;
    }
    setItems((prev) =>
      updateData(
        prev?.length
          ? [...prev, { id: prev.length + 1, ...payload }]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editItem(null);
    setEditClicked(false);
  };

  const editItem = (list: any) => {
    if (!list) {
      setSelectedId(null);
      setEditClicked(false);
      setSubCategoryNumber("");
      setSubCategoryName(null);
      setActive(true);
      setNumberError(false);
      setNameError(false);
      setNameErrorMessage("");
      return;
    }

    list?.id && setSelectedId(list.id);
    setEditClicked(true);
    setSubCategoryNumber(parseRetailSubCategoryLevelFromRow(list));
    setSubCategoryName(getRetailSubCategoryNameFromRow(list));
    setActive(isRetailSubCategoryRowActive(list));
  };

  const deleteItemById = (id: number) => {
    setItems(deleteItem(items, id));
  };

  const payload = buildRetailSubCategoryPayload(
    subCategoryNumber,
    subCategoryName,
    active,
  );

  const dndItemData = useMemo(
    () => formatRetailSubCategoriesForDnd(items),
    [items],
  );

  return (
    <Stack direction="column" gap="10px">
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Stack
          direction="row"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" sx={textStyle}>
            {RETAIL_SUB_CATEGORY_PAGE_TITLE}
            <Tooltip
              title={tooltipComponent(RETAIL_SUB_CATEGORY_TOOLTIPS.header)}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="row" gap="10px">
            {editClicked && (
              <ClearButton
                click={() => {
                  editItem(null);
                  setSelectedId(null);
                }}
              />
            )}
            {lastUpdatedRecord && (
              <CopyButton click={() => editItem(lastUpdatedRecord)} />
            )}
          </Stack>
        </Stack>

        <Stack direction="column" width="100%" gap="16px">
          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>{RETAIL_SUB_CATEGORY_LABEL_NUMBER}</FormLabel>
              <Tooltip
                title={tooltipComponent(RETAIL_SUB_CATEGORY_TOOLTIPS.number)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <FormControl size="small" fullWidth error={numberError}>
              <Select
                displayEmpty
                value={subCategoryNumber}
                onChange={(e) => {
                  setSubCategoryNumber(
                    e.target.value === ""
                      ? ""
                      : (Number(e.target.value) as RetailSubCategoryLevel),
                  );
                  setNumberError(false);
                }}
                renderValue={(selected) =>
                  selected
                    ? getRetailSubCategoryLevelLabel(
                        selected as RetailSubCategoryLevel,
                      )
                    : RETAIL_SUB_CATEGORY_SELECT_LEVEL_PLACEHOLDER
                }
              >
                <MenuItem value="">
                  <em>{RETAIL_SUB_CATEGORY_SELECT_LEVEL_PLACEHOLDER}</em>
                </MenuItem>
                {RETAIL_SUB_CATEGORY_LEVELS.map((level) => (
                  <MenuItem key={level} value={level}>
                    {getRetailSubCategoryLevelLabel(level)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>{RETAIL_SUB_CATEGORY_LABEL_NAME}</FormLabel>
              <Tooltip
                title={tooltipComponent(RETAIL_SUB_CATEGORY_TOOLTIPS.name)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <TextField
              variant="outlined"
              size="small"
              value={subCategoryName ?? ""}
              error={nameError}
              helperText={nameError ? nameErrorMessage : ""}
              onChange={(e) => {
                setSubCategoryName(e.target.value || null);
                setNameError(false);
                setNameErrorMessage("");
              }}
              fullWidth
            />
          </Stack>

          <Stack direction="row" alignItems="center">
            <FormControlLabel
              control={
                <Switch checked={active} onChange={() => setActive(!active)} />
              }
              label={RETAIL_SUB_CATEGORY_LABEL_ACTIVE}
            />
            <Tooltip
              title={tooltipComponent(RETAIL_SUB_CATEGORY_TOOLTIPS.active)}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Stack>
        </Stack>

        <Stack direction="row" justifyContent="flex-end" mt={2}>
          <Button
            variant="contained"
            sx={{
              cursor: "pointer",
              border: "1px solid gray",
              borderRadius: "10px",
              background: "darkgreen",
              color: "white",
            }}
            onClick={() => (selectedId ? updateItem() : addItem())}
          >
            {selectedId ? buttonValues.UPDATE : buttonValues.ADD}{" "}
            {RETAIL_SUB_CATEGORY_BUTTON_SUFFIX}
          </Button>
        </Stack>

        <Stack direction="column" gap="10px" mt={4}>
          <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
            {RETAIL_SUB_CATEGORY_SAMPLE_WARNING}
          </Typography>
          <Typography variant="h6">{RETAIL_SUB_CATEGORY_LIST_TITLE}</Typography>
          {items?.length ? (
            <DraggableItemList
              itemData={dndItemData}
              onOrderUpdate={(order) =>
                setItems(stripRetailSubCategoryDndDisplayFields(order))
              }
              onEdit={editItem}
              onDelete={deleteItemById}
              code={RETAIL_SUB_CATEGORY_DND_LEVEL_LABEL}
              name={RETAIL_SUB_CATEGORY_FIELD_NAME}
            />
          ) : (
            <Typography variant="body2">
              {RETAIL_SUB_CATEGORY_EMPTY_LIST_MESSAGE}
            </Typography>
          )}
        </Stack>
      </Paper>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default RetailSubCategories;
